<template>
  <div>
    <!-- Expand/Collapse Buttons -->
    <div v-if="flatStaffList.length > 0" class="list-btn-bar mt-3 mb-2">
      <v-btn class="fbtn fbtn-expand mr-2" @click="expandAll">
        <v-icon size="15" class="mr-1">mdi-arrow-expand-vertical</v-icon>
        Expand All
      </v-btn>
      <v-btn class="fbtn fbtn-collapse" @click="collapseAll">
        <v-icon size="15" class="mr-1">mdi-arrow-collapse-vertical</v-icon>
        Collapse All
      </v-btn>
    </div>

    <!-- Staff List -->
    <div v-if="displayedStaffList.length > 0" class="mt-1">
      <StaffNode
        v-for="item in displayedStaffList"
        :key="item.id"
        :item="item"
        :search-query="searchQuery"
        @view-details-node="handleCardClick"
      />
    </div>

    <!-- Empty State -->
    <div v-else class="text-center pa-8">
      <v-icon size="56" color="grey lighten-2">mdi-account-off</v-icon>
      <div class="mt-2" style="font-size:0.88rem;color:#90a4ae;">No staff found</div>
    </div>
  </div>
</template>

<script>
import StaffNode from "./StaffNode.vue";

export default {
  components: {
    StaffNode,
  },
  props: {
    staffList: {
      type: Array,
      required: true,
      default: () => [],
    },
    category: {
      type: String,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    expandAllFlag: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      flatStaffList: [],
      expandedIds: Object.create(null),
      idMap: Object.create(null),
      childrenMap: Object.create(null),
      expandedPanels: [],
    };
  },

  computed: {
    displayedStaffList() {
      if (!this.searchQuery) {
        return this.flatStaffList;
      }

      // Filter the hierarchical tree recursively
      return this.filterTreeRecursively(this.flatStaffList, this.searchQuery);
    },
  },

  watch: {
    staffList: {
      async handler(newVal) {
        if (newVal && newVal.length > 0) {
          console.log("Building flat list from staff list:", newVal.length);

          this.cleanupData();
          await this.buildFlatList(newVal);
        }
      },
      immediate: true,
    },
    expandAllFlag(newVal) {
      if (newVal) {
        this.expandAll();
      } else {
        this.collapseAll();
      }
    },
  },

  methods: {
    filterTreeRecursively(nodes, query) {
      if (!nodes || nodes.length === 0) return [];

      const filtered = [];
      
      for (const node of nodes) {
        // Check if this node matches
        const nodeMatches = this.itemMatchesSearch(node, query);
        
        // Recursively filter children
        const filteredChildren = node.members && node.members.length > 0
          ? this.filterTreeRecursively(node.members, query)
          : [];
        
        // Include node if it matches OR has matching children
        if (nodeMatches || filteredChildren.length > 0) {
          // Create a shallow copy with filtered children
          const filteredNode = {
            ...node,
            members: filteredChildren,
            childCount: filteredChildren.length,
            hasChildren: filteredChildren.length > 0,
          };
          
          filtered.push(filteredNode);
        }
      }
      
      return filtered;
    },

    itemMatchesSearch(item, query) {
      const data = item.data;
      query = query.toLowerCase();

      return (
        (data.staffname || "").toLowerCase().includes(query) ||
        (data.staffcode || "").toLowerCase().includes(query) ||
        (data.companyname || "").toLowerCase().includes(query) ||
        (data.branchname || "").toLowerCase().includes(query) ||
        (data.departmentname || "").toLowerCase().includes(query) ||
        (data.sectionname || "").toLowerCase().includes(query) ||
        (data.unitname || "").toLowerCase().includes(query) ||
        (data.gradedesc || "").toLowerCase().includes(query) ||
        (data.posdesc || "").toLowerCase().includes(query) ||
        (data.statusdesc || "").toLowerCase().includes(query)
      );
    },

    cleanupData() {
      this.flatStaffList = [];
      this.idMap = Object.create(null);
      this.expandedIds = Object.create(null);
    },

    async buildFlatList(hierarchicalList) {
      const traverse = async (nodes, depth = 0, parentId = null) => {
        if (!Array.isArray(nodes)) return [];

        const processedNodes = nodes.map(async (node) => {
          const data = {
            staffname: node.leader?.staffname || node.staffname,
            staffcode: node.leader?.staffcode || node.staffcode,
            posdesc: node.leader?.posdesc || node.posdesc,
            statusdesc: node.leader?.statusdesc || node.statusdesc,
            pic: node.leader?.pic || node.pic,
            companyname: node.leader?.companyname || node.companyname,
            branchname: node.leader?.branchname || node.branchname,
            departmentname: node.leader?.departmentname || node.departmentname,
            sectionname: node.leader?.sectionname || node.sectionname,
            unitname: node.leader?.unitname || node.unitname,
            gradedesc: node.leader?.gradedesc || node.gradedesc,
          };

          const hasChildren =
            Array.isArray(node.members) && node.members.length > 0;

          const item = {
            id: node.id,
            parentId,
            depth,
            data,
            hasChildren,
            childCount: hasChildren ? node.members.length : 0,
            expanded: false,
            members: hasChildren
              ? await traverse(node.members, depth + 1, node.id)
              : [],
          };

          this.idMap[item.id] = item;

          if (parentId != null) {
            if (!this.childrenMap[parentId]) this.childrenMap[parentId] = [];
            this.childrenMap[parentId].push(item.id);
          }

          return item;
        });

        return await Promise.all(processedNodes);
      };

      this.flatStaffList = await traverse(hierarchicalList);
    },

    toggleExpand(item) {
      if (item.hasChildren) {
        item.expanded = !item.expanded;
      }
    },

    expandAll() {
      const expandRecursive = (items) => {
        items.forEach((item) => {
          if (item.hasChildren) {
            item.expanded = true;
            expandRecursive(item.members);
          }
        });
      };
      expandRecursive(this.flatStaffList);
    },
    
    collapseAll() {
      const collapseRecursive = (items) => {
        items.forEach((item) => {
          if (item.hasChildren) {
            item.expanded = false;
            collapseRecursive(item.members);
          }
        });
      };
      collapseRecursive(this.flatStaffList);
    },

    handleCardClick(item) {
      if (this.category === "KPI") {
        this.$emit("view-kpi-details", {
          staffid: item.id,
          staffname: item.staffname,
          staffcode: item.staffcode,
        });
      } else if (this.category === "Review") {
        this.$emit("view-review-details", {
          staffid: item.id,
        });
      }
    },
  },

  mounted() {
    console.log("Current category in staff list:", this.category);
  },

  beforeDestroy() {
    console.log("Cleaning up MyStaffList component");
    this.cleanupData();
  },
};
</script>

<style scoped>
.list-btn-bar {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fbtn {
  min-width: 110px !important;
  height: 34px !important;
  border-radius: 8px !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  box-shadow: none !important;
}

.fbtn-expand {
  background: #F0FDF4 !important;
  color: #15803D !important;
  border: 1px solid #BBF7D0 !important;
}
.fbtn-expand:hover {
  background: #DCFCE7 !important;
}

.fbtn-collapse {
  background: #F1F5F9 !important;
  color: #475569 !important;
  border: 1px solid #E2E8F0 !important;
}
.fbtn-collapse:hover {
  background: #E2E8F0 !important;
}
</style>